'use client';
import { useEffect } from 'react';
import { Box, Typography, Container, Paper } from '@mui/material';
import TopNavigation from '@/components/TopNavigation';
import Footer from '@/components/Footer';
import Link from 'next/link';

export default function TermsAndConditions() {
  useEffect(() => {
    document.title = 'CareAscendium - Terms and Conditions';
  }, []);
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <TopNavigation />

      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 50%, #2A5A9C 100%)',
        py: 8, 
        px: 4,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h1" 
            sx={{ 
              color: 'white', 
              mb: 4,
              fontWeight: 800,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            Terms and Conditions
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            Please read these terms carefully
          </Typography>
        </Container>
      </Box>

      {/* Terms and Conditions Content */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 4, md: 6 },
            bgcolor: 'white',
            borderRadius: 3,
            border: '1px solid rgba(21,60,127,0.1)'
          }}
        >
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              Please read the following Terms of Use ("Terms") carefully. These Terms will govern your use of CareAscendium, LLC's ("CareAscendium") web and/or mobile application (collectively, the "Application"). If you do not agree to be bound by these Terms, please promptly cease all use of the Application. By continuing to register and/or use the Application, you are agreeing to fully comply with these Terms.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              CareAscendium reserves the right to change these Terms or other applicable terms and conditions of use of the Application in its sole and absolute discretion at any time with or without notice. It is your responsibility to review these Terms for any changes each time that you use the Application. All changes to the Terms are effective from the date posted.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Registration for the Application
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              The Application is intended for use by healthcare professionals, specifically medical directors and other healthcare administrators and their staffs, to manage tasks and better meet regulatory standards. In order to access and use the Application, you must set up a user account ("Your Account") and abide by these Terms and other applicable terms and conditions. In setting up and maintaining Your Account, you agree to provide accurate information regarding your identity, your contact information, your job description and any other information requested by CareAscendium. You will set your own password for accessing the Service and shall be solely and strictly liable for everything that occurs through the use of Your Account. You are responsible for maintaining the confidentiality of your password and logging out of the Application when you are done using it. You agree to immediately notify CareAscendium of any unauthorized use of your password or Your Account, or any other breach of security of which you become aware or reasonably suspect.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              The Application is not intended for use by individuals under the age of 13. If you are under 13, do not use or provide any information on the Application.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Use of the Application
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              The Application may only be used for legal and appropriate uses for which the application is intended and in accordance with these Terms and other applicable terms and conditions. CareAscendium reserves the right to make changes to the Application at any time and without notice. Your access to and use of the Application may be blocked, suspended, or terminated without notice at any time for any reason or no reason at all. Although the following list is not exhaustive, your access to the Application may be limited or revoked by CareAscendium at any time for any violation of the following rules:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                You must comply with all applicable state, federal, and/or international laws, rules, policies and/or licenses while using the Application, including but not limited to, telecommunications laws, U.S. export laws (e.g. Export Administration Act and the Export Administration Regulations administered by the Department of Commerce), the Health Insurance Portability and Accountability Act and its implementing administrative simplification regulations (45 CFR 160-164) ("HIPAA"), and applicable patent, copyright, trademark and other intellectual property laws, and intellectual property rights of CareAscendium and third parties. If you are a covered entity under HIPAA, your use is governed by our Business Associate Agreement (BAA).
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                You may not attempt to remove, alter, cover, or distort any copyright, trademark, or other proprietary rights notice on the Application or materials that CareAscendium may provide or make available through the Application;
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                You may not interfere or attempt to interfere with the Application or another person's use of the Application by use of any program, script, command, device, software, routine, or otherwise.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                You may not circumvent, disable or otherwise interfere with security-related features of the Application including, without limitation, any features that prevent or restrict use or copying of content or enforce limitations on the use of the Application.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                You may not collect or harvest any personally identifiable information from the Application including, without limitation, user names, passwords, email addresses, IP addresses, service and accounting information;
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                You may not copy, sell, transfer, rent, share, reverse engineer, decompile, copy, duplicate, modify or translate any portion of the Application, including screens or content therein, or any algorithms used in creating, or used in conjunction with, the Application, or create derivative works based upon the Application, or create a service bureau using the Application.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                You may not use the application to store or transmit credit card numbers, financial account numbers, or other similarly sensitive personal information including personally identifiable information (PII).
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                Except for instances where specifically authorized by CareAscendium, and then only in the limited manner authorized, you may not use the application to store or transmit protected health information (PHI) as that term is defined under HIPAA. You are responsible to ensure that your collection and use of PHI will be in compliance with all applicable laws and regulations.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                You may only use the Application inside the United States of America, excluding any territories and freely associated states.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                You may not use the Application if you are under the age of 18.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                You may not use the Application for the purpose of developing an application that is similar in nature or purpose to the Application.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                You may not engage in any activities that constitutes, in CareAscendium sole discretion, an abuse of the Application, including but not limited to spamming, harassment, or uploading malicious content.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                You agree to fully cooperate with CareAscendium's investigation of any suspected or actual activity that is in breach of these Terms.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              License and Ownership
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              CareAscendium grants you a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Application in the United States in connection with the performance of your healthcare administrative tasks and in accordance with these Terms and other applicable terms and conditions.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              Any unauthorized use of the Application, including violation of these Terms or other applicable terms and conditions, shall automatically terminate the license granted to you herein. Further, CareAscendium reserves the right to revoke this license at any time and for any reason without notice. All rights in the Application not expressly granted herein shall be deemed as reserved exclusively for CareAscendium. You will not acquire any right, title or interest in or to the Application by reason of these Terms or the use of the Application or in any other manner, except for the non-exclusive license to use the Application in accordance with these Terms.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Feedback and Results
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              You may be asked to, or may voluntarily, provide suggestions, feedback or other ideas relating to your use of the Application (collectively, the "Feedback"), which may be used by CareAscendium to improve the Application or for any other purpose. In addition, CareAscendium may track and/or aggregate information relating to your use of the Application (collectively, the "Results"). You agree that as it pertains to the Feedback and Results as well as any modifications or improvements to the Application or any other inventions, ideas, intellectual property rights or similar creations by CareAscendium based on the Feedback or Results, you (i) hereby waive all rights therein, (ii) you shall not have any claim to compensation relating to use thereof, (iii) you shall not have and shall not claim any ownership rights therein.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Links
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              The Application may contain links to third-party sites. The links are provided "as is." CareAscendium does not endorse, and CareAscendium is not liable or responsible for any content, products, services, software or other materials available on such other sites, even if a page or pages of the other sites are framed within the Application. CareAscendium is not responsible for the privacy practices or the content of other sites. CareAscendium shall not be responsible or liable to you or any third party, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with use of or reliance on any such third-party links including but not limited to content, goods or services available on such other sites. CareAscendium shall not be liable for any errors or delays in the content, goods or services available on such other sites, or for any actions taken or not taken in reliance thereon.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Disclaimer of Warranties
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              WHILE CAREASCENDIUM ENDEAVORS TO PROVIDE ACCURATE INFORMATION, CAREASCENDIUM DOES NOT GUARANTEE THAT THE INFORMATION OBTAINED THROUGH THE APPLICATION IS ACCURATE, UP-TO-DATE, COMPLETE, RELIABLE, OR OTHERWISE VALID. CAREASCENDIUM ALSO ASSUMES NO RESPONSIBILITY FOR IF AND/OR HOW YOU USE THE INFORMATION AND MATERIALS THAT YOU OBTAIN VIA THE APPLICATION. IT IS SOLELY YOUR RESPONSIBILITY TO ENSURE THAT YOUR ORGANIZATION MEETS ALL REGULATORY REQUIREMENTS. THE APPLICATION IS NOT INTENDED TO PROVIDE MEDICAL ADVICE OR REPLACE PROFESSIONAL JUDGMENT. TO THE EXTENT PERMITTED BY APPLICABLE LAW, THE APPLICATION IS PROVIDED "AS IS," WITHOUT WARRANTY OF ANY KIND, EITHER EXPRESS OR IMPLIED. CAREASCENDIUM DOES NOT GUARANTEE, REPRESENT, OR WARRANT THAT THE USE OF THE APPLICATION WILL BE UNINTERRUPTED, ERROR FREE, OR VIRUS FREE, OR THAT THE APPLICATION WILL MEET YOUR REQUIREMENTS. CAREASCENDIUM DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO ANY WARRANTY OF TITLE, NON-INFRINGEMENT, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR ANY WARRANTY ARISING FROM A COURSE OF DEALING, TRADE USAGE, OR TRADE PRACTICE. WE DO NOT WARRANT COMPLIANCE WITH ACCESSIBILITY STANDARDS LIKE WCAG, THOUGH WE STRIVE FOR IT.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Limitation of Liability
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              YOU ACKNOWLEDGE AND AGREE THAT YOUR USE OF THE APPLICATION IS AT YOUR SOLE RISK. NEITHER CAREASCENDIUM, NOR ANY OF ITS MEMBERS, MANAGERS, EMPLOYEES, AFFILIATES, AGENTS, REPRESENTATIVES, LICENSORS, SUCCESSORS, OR ASSIGNS (COLLECTIVELY, "CAREASCENDIUM ASSOCIATES") SHALL BE LIABLE TO YOU FOR ANY COSTS, COMPENSATORY, DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL DAMAGES, OR ATTORNEYS' FEES, OR FOR LOST DATA, LOST PROFIT, INJURY OR BODILY HARM, ARISING OUT OF: (A) THE USE OF THE APPLICATION OR THE MATERIALS AND INFORMATION THEREIN; (B) THE INABILITY TO GAIN ACCESS TO OR USE THE APPLICATION; (C) YOUR ORGANIZATION'S FAILURE TO MEET ANY REGULATORY REQUIREMENTS; AND/OR (D) CAREASCENDIUM'S BREACH OF THESE TERMS. THE FOREGOING EXCLUSIONS FROM LIABILITY SHALL BE APPLICABLE EVEN IF CAREASCENDIUM HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES OR SUCH DAMAGES WERE FORESEEABLE. YOU ACKNOWLEDGE THAT INFORMATION TRANSMITTED AND/OR MAINTAINED THROUGH THE INTERNET IS NEVER COMPLETELY SECURE. UNDER NO CIRCUMSTANCES SHALL CAREASCENDIUM BE LIABLE FOR THE UNAUTHORIZED DISCLOSURE OF INFORMATION THAT IS TRANSMITTED BY YOU VIA THE APPLICATION. YOUR SOLE RIGHT AND REMEDY WITH RESPECT TO ANY DISPUTE WITH CAREASCENDIUM IS TO STOP USING THE APPLICATION. OUR LIABILITY IS CAPPED AT FEES PAID BY YOU IN THE PRIOR 6 MONTHS. BECAUSE SOME STATES OR JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF LIABILITY FOR CONSEQUENTIAL, INCIDENTAL OR SPECIAL DAMAGES, IN SUCH STATES AND JURISDICTIONS CAREASCENDIUM AND CAREASCENDIUM ASSOCIATES LIABILITY IS LIMITED TO THE GREATEST EXTENT PERMITTED BY LAW.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Indemnification
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              You agree to indemnify, defend and hold harmless CareAscendium and CareAscendium Associates from and against all losses, liabilities, expenses, damages, claims, demands and costs, including reasonable attorneys' fees and court costs, relating to or arising from any violation of these Terms by you.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Privacy Policy
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              CareAscendium respects and is committed to your privacy. Please review CareAscendium's Privacy Policy at{' '}
              <Link 
                href="/privacy-policy" 
                style={{ 
                  color: '#153C7F',
                  textDecoration: 'none',
                  fontWeight: 600
                }}
              >
                https://careascend.com/privacy-policy
              </Link>
              , which also governs your use of the Application, to understand CareAscendium's practices. By registering for or using the Application, you are also signifying your acknowledgement and consent to the data practices described in CareAscendium's Privacy Policy.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Miscellaneous
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              The laws of the State of Indiana shall govern the validity, performance, enforcement, interpretation and any other aspect of these Terms, without regard to principles of conflicts of laws thereunder. The parties agree to submit to the exclusive jurisdiction and venue of the courts of Hamilton County, Indiana for any action arising out of these Terms. CareAscendium shall be entitled to payment from you for any and all out-of-pocket costs, including, without limitation, attorneys' fees, incurred by CareAscendium in connection with enforcing these Terms. Disputes shall first attempt mediation; if unresolved, binding arbitration in Hamilton County under AAA rules, with prevailing party recovering fees.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              You acknowledge and agree that the provisions, disclosures and disclaimers set forth in these Terms are fair and reasonable and your agreement to follow and be bound by them is not the result of fraud, duress or undue influence exercised upon you by any person or entity. The failure of CareAscendium to exercise or enforce any right or provision of these Terms shall not constitute a waiver of such right or provision. If any provision of these Terms is found by a court of competent jurisdiction to be invalid, the parties nevertheless agree that the court should endeavor to give effect to the parties' intentions as reflected in the provision, and the other provisions of the Terms shall remain in full force and effect.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              CareAscendium shall have the right to assign these Terms and to sublicense any and all of its rights under these Terms. These Terms, including any documents referenced herein and any additional terms and conditions as posted via the Application, represent the entire understanding between you and CareAscendium regarding the Application. These Terms supersede all previous written or oral agreements between you and CareAscendium with respect to such subject matter. Notwithstanding any provision of these Terms, CareAscendium has available all remedies at law or equity to enforce these Terms. Electronic signatures are binding. Terms survive termination where applicable (e.g., IP, liability).
            </Typography>
          </Box>

          <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(21,60,127,0.1)' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2,
                color: '#666',
                fontSize: '0.9rem',
                fontStyle: 'italic'
              }}
            >
              Rev. 10/30/2025
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2,
                color: '#666',
                fontSize: '0.9rem',
                fontWeight: 600
              }}
            >
              © CAREASCENDIUM, LLC
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1,
                color: '#666',
                fontSize: '0.9rem'
              }}
            >
              <Link 
                href="https://www.CareAscendium.com" 
                style={{ 
                  color: '#153C7F',
                  textDecoration: 'none'
                }}
              >
                www.CareAscendium.com
              </Link>
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1,
                color: '#666',
                fontSize: '0.9rem',
                fontStyle: 'italic'
              }}
            >
              Confidential and Proprietary
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1,
                color: '#666',
                fontSize: '0.9rem'
              }}
            >
              <Link 
                href="mailto:info@CareAscendium.com" 
                style={{ 
                  color: '#153C7F',
                  textDecoration: 'none'
                }}
              >
                info@CareAscendium.com
              </Link>
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#666',
                fontSize: '0.9rem'
              }}
            >
              12486 Shadow Cove Way, Carmel, IN 46033
            </Typography>
          </Box>
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
}

